import React, { useCallback, useEffect, useMemo } from "react";
import PublishStep from "./steps/publishStep/PublishStep";
import InfoStep from "./steps/infoStep/InfoStep";
import Wrapper from "components/commons/wrapper/Wrapper";
import Header, { NavType } from "components/commons/header/Header";
import { strings } from "intl/fa";
import { useAppDispatch, useAppSelector } from "utils/store";
import { OrderAdActions, OrderAdPages } from "core/orderAd/orderAd.slice";
import SelectAdType from "../selectAdSpot/SelectAdSpot";
import {
  AppPage,
  NavigationActions,
} from "../../../core/navigation/navigation.slice";
import AdOrderBreadcrumb from "./AdOrderBreadcrumb";
import OrderPreview from "./steps/orderPreview/OrderPreview";
import ContactInfoForm from "./steps/contactInfoForm/ContactInfoForm";
import { AdSpot } from "../../../core/orderAd/orderAd.entity";
import { useNeedAuth } from "hooks/useNeedAuth";
import MainInfo from "../sponsoredMessage/steps/1_mainInfo/MainInfo";
import ViewOptions from "../sponsoredMessage/steps/2_viewOptions/ViewOptions";
import Preview from "../sponsoredMessage/steps/4_preview/Preview";
import { SponsoredInfo } from "../new_flow/info/SponsoredInfo";
import { SponsoredImageStep } from "../new_flow/info/SponsoredImageStep";
import { SponsoredDetails } from "../new_flow/info/SponsoredDetails";
import { SponsoredFinalStep } from "../new_flow/info/SponsoredFinalStep";
import SettingsStep from "./steps/settingsStep/SettingsStep";

const OrderAdWrapper: React.FC<React.PropsWithChildren<unknown>> = () => {
  const dispatch = useAppDispatch();
  const history = useAppSelector((state) => state.orderAd.history);
  const page = useAppSelector((state) => state.orderAd.page);

  const { isOrganization, needsAuth, isAboveThreshold } = useNeedAuth();

  useEffect(() => {
    if (
      (isOrganization && needsAuth) ||
      (!isOrganization && needsAuth && isAboveThreshold)
    ) {
      // TODO:
      dispatch(NavigationActions.setAppPage(AppPage.Invoicing));
    }
  }, [needsAuth, isOrganization]);

  const CurrentPage = useMemo(() => {
    switch (page) {
      case OrderAdPages.CampaignSelectAdType:
        return <SelectAdType isCampaign={true} />;
      case OrderAdPages.SelectAdType:
        return <SelectAdType />;
      case OrderAdPages.DialogMainInfo:
        return <InfoStep />;
      case OrderAdPages.DialogMoreInfo:
        return <PublishStep />;
      case OrderAdPages.DialogMoreSettings:
        return <SettingsStep />;
      case OrderAdPages.DialogPreview:
        return <OrderPreview />;

      case OrderAdPages.CampaignMainInfo:
        return <InfoStep isCampaign />;
      case OrderAdPages.CampaignMoreInfo:
        return <PublishStep isCampaign />;
      case OrderAdPages.CampaignMoreSettings:
        return <SettingsStep isCampaign />;
      case OrderAdPages.CampaignPreview:
        return <OrderPreview isCampaign />;

      case OrderAdPages.SubmitOnboardingAd:
        return <ContactInfoForm adSpot={AdSpot.AdSpotOnBoarding} />;
      case OrderAdPages.SubmitMyBankAd:
        return <ContactInfoForm adSpot={AdSpot.AdSpotMyBank} />;

      case OrderAdPages.SponsoredMessageMainInfo:
        return <SponsoredInfo />;

      case OrderAdPages.SponsoredFinalStep:
        return <SponsoredFinalStep />;

      case OrderAdPages.SponsoredDetails:
        return <SponsoredDetails />;

      case OrderAdPages.SponsoredMessageImageStep:
        return <SponsoredImageStep />;

      case OrderAdPages.SponsoredFinalStep:
        return <ViewOptions isCampaign />;

      // case OrderAdPages.SponsoredMessageMainInfo:
      //   return <MainInfo />;
      case OrderAdPages.CampaignSponsoredMessageMainInfo:
        return <MainInfo isCampaign />;
      case OrderAdPages.SponsoredMessageViewOptions:
        return <ViewOptions />;

      case OrderAdPages.CampaignSponsoredMessageViewOptions:
        return <ViewOptions isCampaign />;
      // case OrderAdPages.SponsoredMessageMoreinfo:
      //   return <MoreInfo />;
      case OrderAdPages.SponsoredMessagePreview:
        return <Preview />;
      case OrderAdPages.CampaignSponsoredMessagePreview:
        return <Preview isCampaign />;
    }
  }, [page]);
  const currentPageTitle = useMemo(() => {
    switch (page) {
      case OrderAdPages.SelectAdType:
        return strings.order_ad.title.choose_ad_type;
      case OrderAdPages.CampaignSelectAdType:
        return strings.order_ad.title.choose_ad_type;
      case OrderAdPages.DialogMainInfo:
      case OrderAdPages.DialogMoreInfo:
      case OrderAdPages.DialogMoreSettings:
        return strings.order_ad.title.dialog;
      case OrderAdPages.CampaignMainInfo:
      case OrderAdPages.CampaignMoreInfo:
        return strings.order_ad.title.campaign;
      case OrderAdPages.DialogPreview:
      case OrderAdPages.CampaignPreview:
        return strings.order_ad.title.submit;
      case OrderAdPages.Invoice:
        return strings.order_ad.title.invoice;
      case OrderAdPages.AdditionalInfo:
        return strings.order_ad.title.additional_info;
      case OrderAdPages.SubmitMyBankAd:
      case OrderAdPages.SubmitOnboardingAd:
        return strings.order_ad.title.contact_info;
      case OrderAdPages.CampaignSponsoredMessageMainInfo:
      case OrderAdPages.SponsoredMessageMainInfo:
      case OrderAdPages.SponsoredMessageViewOptions:
        return strings.order_ad.title.sponsored_info;
      case OrderAdPages.SponsoredMessagePreview:
        return strings.order_ad.title.submit;
      case OrderAdPages.SponsoredDetails:
        return strings.order_ad.title.sponsored_details_step;
      case OrderAdPages.SponsoredMessageImageStep:
        return strings.order_ad.title.sponsored_details_step;
      case OrderAdPages.SponsoredFinalStep:
        return strings.order_ad.title.sponsored_final_step;
      default:
        return strings.order_ad.title.choose_ad_type;
    }
  }, [page]);

  const onClose = useCallback(() => {
    dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
    dispatch(OrderAdActions.resetState());
  }, []);

  const onBack = useCallback(() => {
    if (history.length > 1) {
      dispatch(OrderAdActions.goBack());
    } else {
      dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
      dispatch(OrderAdActions.resetState());
    }
  }, [page, history]);

  return (
    <Wrapper>
      <Header
        onClose={onClose}
        onBack={onBack}
        navType={NavType.both}
        title={currentPageTitle}
      />
      <AdOrderBreadcrumb />
      {CurrentPage}
    </Wrapper>
  );
};

export default OrderAdWrapper;
